import { CompanyContactPersonDto } from "Api/Api";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch, useHistory } from "react-router-dom";
import {
  getCompaniesDetailAsync,
  openCompanyContactPersonDeleteModal,
  openCompanyContactPersonModal,
  saveCompanyContactPersonAsync,
} from "State/Companies/CompanyReducer";
import { RootStateType } from "State/Store";
import { Resources, useResource } from "Translations/Resources";
import styled from "styled-components";
import { setDisableRedirect } from "State/App/AppReducer";
import { BreadcrumbItem } from "Components/Layout/Breadcrumbs";
import { isNoU } from "Utils/ObjectUtils";
import { DefaultButtonWithTooltip } from "Components/Shared/Buttons/DefaultButtonWithTooltip";
import {
  Column,
  PrimaryDataTable,
  TableIDType,
} from "Components/Shared/PrimaryDataTable/PrimaryDataTable";
import { EditIcon, PlusIcon, TrashCanIcon } from "Components/Shared/Icons";
import { CompaniesSideMenu } from "Components/Companies/CompaniesSideMenu";
import { PrimarySkeleton } from "Components/Shared/PrimarySkeleton";
import { CompanyContactEdit } from "Components/Companies/CompanyContacts/CompanyContactEdit";
import { CompanyContactDeleteConfirmModal } from "Components/Companies/CompanyContacts/CompanyContactDeleteConfirmModal";
import { Authorized } from "Routes/Authorized";
import { necessaryAccessRightEditForCompanies } from "Hooks/useHasCurrentUserAccessRight";

type RouteProps = {
  id: string;
};

type Props = {
  companiesListUrl: string;
};

const StyledLayout = styled.div`
  ${props => props.theme.breakpoints.down("xs")} {
    h3 {
      padding: ${props => props.theme.spacing(0, 1)};
    }
  }
`;

const StyledTableWrapper = styled.div`
  .table-wrapper .table tbody > tr > td:last-child {
    width: 8%;
    padding-right: 8px;
    padding-left: 10px;
  }
`;

const StyledPlusIcon = styled(PlusIcon)`
  width: 18px;
  height: 18px;
  path {
    fill: white;
  }
`;
const StyledActionWrapper = styled.div`
  > div {
    float: right;
  }
`;
const StyledEditIcon = styled(EditIcon)`
  path {
    fill: #6d7278;
  }
`;
const StyledDeleteIcon = styled(TrashCanIcon)`
  path {
    fill: #6d7278;
  }
`;

export const CompanyContactsPage: React.FunctionComponent<Props> = props => {
  const { t } = useResource();
  const dispatch = useDispatch();
  const history = useHistory();
  const { params } = useRouteMatch<RouteProps>();
  const companyID = params.id as any;

  const { companyDetail, isLoading, editedCompanyContactPerson } = useSelector(
    (e: RootStateType) => e.companies,
  );

  const isMarketplaceSupplierCompany =
    companyDetail !== null &&
    companyDetail.isMarketplaceEnabled &&
    companyDetail.isSupplier;

  React.useEffect(() => {
    dispatch(getCompaniesDetailAsync.request(companyID));
  }, [dispatch, companyID]);

  React.useEffect(() => {
    if (companyDetail?.companyID) {
      dispatch(setDisableRedirect(false));
    }
  }, [dispatch, companyDetail, history, t]);

  const getBreadcrumbItems = () => {
    return [
      {
        name: t(Resources.Companies.Title),
        url: t(Resources.Routes.Companies),
      } as BreadcrumbItem,
      {
        name: t(Resources.CompanyEdit.Breadcrumb.Title),
      } as BreadcrumbItem,
    ];
  };

  const getCell = (
    id: TableIDType<CompanyContactPersonDto>,
    dto: CompanyContactPersonDto,
  ) => {
    switch (id) {
      case "firstName":
        return <div>{`${dto.firstName} ${dto.lastName}`}</div>;
      case "email":
        return <div>{dto.email}</div>;
      case "phone":
        return <div>{dto.phone}</div>;
      case "positionName":
        return <div>{dto.positionName}</div>;
      case "detail":
        return (
          <Authorized
            accessRights={necessaryAccessRightEditForCompanies(
              companyDetail?.isCustomer,
              companyDetail?.isSupplier,
              companyDetail?.isMarketplaceEnabled,
            )}
          >
            <StyledActionWrapper>
              <DefaultButtonWithTooltip
                title={"Smazat"}
                onClick={() => {
                  dispatch(
                    openCompanyContactPersonDeleteModal(
                      dto.companyContactPersonID,
                    ),
                  );
                }}
              >
                <StyledDeleteIcon></StyledDeleteIcon>
              </DefaultButtonWithTooltip>

              <DefaultButtonWithTooltip
                title={"Editace"}
                onClick={() => {
                  dispatch(
                    openCompanyContactPersonModal({
                      companyID: companyID,
                      editedPerson: dto,
                    }),
                  );
                }}
              >
                <StyledEditIcon></StyledEditIcon>
              </DefaultButtonWithTooltip>
            </StyledActionWrapper>
          </Authorized>
        );
      default:
        return <div>-</div>;
    }
  };

  let columns: Column<CompanyContactPersonDto>[] = [
    {
      id: "firstName",
      label: "Jméno",
      width: "30%",
    },
    {
      id: "email",
      label: "E-mail",
      width: "25%",
    },
    {
      id: "positionName",
      label: "Pozice",
      width: "30%",
    },
    {
      id: "detail",
      label: "",
      width: "5%",
    },
  ];

  if (!isMarketplaceSupplierCompany) {
    columns = columns.filter(e => e.id !== "positionName");
  }

  return (
    <StyledLayout>
      <AuthenticatedLayout
        title={companyDetail?.name ?? ""}
        hasSideMenu={!isNoU(companyID)}
        breadcrumbItems={getBreadcrumbItems()}
      >
        <CompaniesSideMenu companyID={companyID} />

        {companyDetail && companyDetail.contacts && (
          <div className={"container-small"}>
            <div className={"box"}>
              <div className={"box-header"}>
                {!isLoading && companyDetail ? (
                  t(Resources.CompanyEdit.Title)
                ) : (
                  <PrimarySkeleton height={30} />
                )}
                <div className="box-header-buttons">
                  <Authorized
                    accessRights={necessaryAccessRightEditForCompanies(
                      companyDetail?.isCustomer,
                      companyDetail?.isSupplier,
                      companyDetail?.isMarketplaceEnabled,
                    )}
                  >
                    <button
                      className="bl-btn bl-btn-orange edit"
                      type="button"
                      onClick={() => {
                        dispatch(
                          openCompanyContactPersonModal({
                            companyID: companyID,
                            editedPerson: null,
                          }),
                        );
                      }}
                    >
                      <StyledPlusIcon></StyledPlusIcon>
                    </button>
                  </Authorized>
                </div>
              </div>
              <StyledTableWrapper className={"box-content"}>
                <PrimaryDataTable
                  isLoading={isLoading}
                  rows={companyDetail.contacts.map(e => ({
                    data: e,
                    rowEntityID: "companyContactPersonID",
                  }))}
                  getHeaderCell={column => <div>{column.label}</div>}
                  getCell={getCell}
                  columns={columns}
                ></PrimaryDataTable>
              </StyledTableWrapper>
            </div>
          </div>
        )}

        {editedCompanyContactPerson && (
          <CompanyContactEdit
            person={editedCompanyContactPerson}
            isMarketplaceSupplierCompany={isMarketplaceSupplierCompany}
            onSave={dto => {
              dispatch(saveCompanyContactPersonAsync.request(dto));
            }}
          ></CompanyContactEdit>
        )}

        <CompanyContactDeleteConfirmModal></CompanyContactDeleteConfirmModal>
      </AuthenticatedLayout>
    </StyledLayout>
  );
};
